import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import ValidateSystemNameCommand from 'features/systems/domain/models/validate-system-name-command';
import { ReadSystemsQuery } from '../models/read-systems-query';
import { ReadSystemsResponse } from '../models/read-systems-response';
import { System } from '../models/system';
import SystemMonitoringFilter from '../models/system-monitoring-filter';

const baseUrl = '/api/v1/System';

enum SystemsQueryPaths {
  GetSystems = 'ReadSystems',
  GetSystem = 'ReadSystem',
  UpdateSystem = 'UpdateSystem',
  DeleteSystem = 'DeleteSystem',
  ValidateDeleteSystem = 'ValidateDeleteSystem',
  GetMonitoringFilters = 'GetMonitoringFilters',
  ValidateSystemName = 'ValidateSystemName',
}

const getSystems = async (axios: AxiosInstance, body: ReadSystemsQuery): Promise<ReadSystemsResponse> => {
  const response = await axios.post(`${baseUrl}/${SystemsQueryPaths.GetSystems}`, body);
  return response.data;
};

const getSystem = async (axios: AxiosInstance, id: string, signal?: AbortSignal): Promise<System> => {
  const response = await axios.get(`${baseUrl}/${SystemsQueryPaths.GetSystem}/${id}`, { signal });
  return response.data;
};

const updateSystem = async (axios: AxiosInstance, body: System): Promise<System> => {
  const response = await axios.put(`${baseUrl}/${SystemsQueryPaths.UpdateSystem}`, body);
  return response.data;
};

const deleteSystem = async (axios: AxiosInstance, id: string): Promise<void> => {
  await axios.delete(`${baseUrl}/${SystemsQueryPaths.DeleteSystem}/${id}`);
};

const validateDeleteSystem = async (axios: AxiosInstance, id: string): Promise<void> => {
  await axios.post(`${baseUrl}/${SystemsQueryPaths.ValidateDeleteSystem}/${id}`);
};

const getMonitorFilters = async (axios: AxiosInstance, signal?: AbortSignal): Promise<SystemMonitoringFilter[]> => {
  const response = await axios.get(`${baseUrl}/${SystemsQueryPaths.GetMonitoringFilters}`, { signal });
  return response.data;
};

export const validateSystemName = async (axios: AxiosInstance, command: ValidateSystemNameCommand): Promise<void> => {
  await axios.post(`${baseUrl}/${SystemsQueryPaths.ValidateSystemName}`, command);
};

export const useLazyGetSystem = (id: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [SystemsQueryPaths.GetSystem, id],
    queryFn: ({ signal }) => getSystem(authenticatedInstance, id, signal),
    enabled: false,
  });
};

export const useGetSystems = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [SystemsQueryPaths.GetSystems],
    mutationFn: (body: ReadSystemsQuery) => getSystems(authenticatedInstance, body),
  });
};

export const useUpdateSystem = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [SystemsQueryPaths.UpdateSystem],
    mutationFn: (body: System) => updateSystem(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SystemsQueryPaths.GetSystems] });
    },
  });
};

export const useDeleteSystem = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [SystemsQueryPaths.DeleteSystem],
    mutationFn: (id: string) => deleteSystem(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SystemsQueryPaths.GetSystems] });
    },
  });
};

export const useValidateDeleteSystem = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [SystemsQueryPaths.ValidateDeleteSystem],
    mutationFn: (id: string) => validateDeleteSystem(authenticatedInstance, id),
  });
};

export const useGetMonitorFilters = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [SystemsQueryPaths.GetMonitoringFilters],
    queryFn: ({ signal }) => getMonitorFilters(authenticatedInstance, signal),
  });
};
